// File: frontend/src/components/marketing/visuals/FeatureVisuals.js
//
// Illustrations for the landing page's feature showcase. Each one shows what a
// feature looks like to an operator, and they are deliberately different from
// the full product-tour screens further down the page.
//
// ── PHOTOGRAPHY FOR THE THING, CODE FOR THE NUMBERS ─────────────────────────
//
// Where a feature is about something physical — routers on a desk, printed
// voucher cards, the login page on a customer's phone, a team at a laptop —
// the visual is a photograph from public/landing (see
// scripts/optimize-landing-assets.py). Where a feature is about figures,
// charts or interface state, it stays drawn in code: a photograph of a chart
// cannot switch theme, goes stale the moment the product changes, and any text
// baked into it is a claim nobody can check. So every scene carries its real
// numbers as HTML beside or over it rather than inside the artwork.
//
// ── THE SCENES ARE CAPPED, AND THE CAP IS A BUDGET ──────────────────────────
//
// These illustrations live in a STICKY panel, so the tallest of them decides
// whether the panel fits on screen. A 1366x768 laptop leaves roughly 660 CSS px
// once browser chrome is gone; minus the header and its offset that is about
// 568px for the whole panel. Every scene is therefore capped at a width that
// keeps its own block under that, rather than being allowed to fill the column.
// If a scene is added or a label list grows, re-measure against 660 — a sticky
// panel taller than the viewport hides its own bottom for the entire section.
//
// Landscape scenes get a separately composed portrait version for phones,
// art-directed with `media` rather than cropped, for the same reason the hero
// does it.
//
// Fictional data only. Everything shown maps to real PesaFii behaviour:
//   · withdrawals go to MTN or Airtel Mobile Money, need the withdrawal PIN,
//     and the fee is quoted before confirming (Terms, withdrawals section);
//   · staff roles are the real role templates (backend/config/permissions.js:
//     Admin, Manager, Voucher Manager, Support Staff), plus voucher agents,
//     who are a separate account type that earns commission;
//   · the login page can carry the operator's own photo, name, colours and
//     button text (PortalCustomizer).

import React from "react";
import { Box, Paper, Stack, Typography, useTheme, alpha } from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { DEMO_LOCATIONS } from "../../../data/marketingDemoData";

function Card({ title, icon: Icon, aside, children, sx }) {
  const theme = useTheme();
  return (
    <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid", borderColor: "divider", minWidth: 0, ...sx }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          {Icon && (
            <Box sx={{ width: 34, height: 34, borderRadius: 2, flexShrink: 0, display: "grid", placeItems: "center", bgcolor: alpha(theme.palette.primary.main, 0.14), color: "primary.main" }}>
              <Icon sx={{ fontSize: 19 }} />
            </Box>
          )}
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: "text.primary" }}>{title}</Typography>
        </Stack>
        {aside}
      </Stack>
      {children}
    </Paper>
  );
}

const Pill = ({ children, tone = "success" }) => (
  <Box component="span" sx={(t) => ({
    px: 1, py: 0.25, borderRadius: 99, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
    color: tone === "muted" ? "text.secondary" : `${tone}.main`,
    bgcolor: alpha(tone === "muted" ? t.palette.text.primary : t.palette[tone].main, 0.12),
  })}>
    {children}
  </Box>
);

const srcset = (name, widths, ext) => widths.map((w) => `/landing/${name}-${w}.${ext} ${w}w`).join(", ");
const PHONES = "(max-width: 899.95px)";

/**
 * A photographic scene in the page's frame treatment.
 *
 * `portrait` is a DIFFERENT composition for phones, not a crop. The wrapper
 * owns the aspect ratio per breakpoint and the image covers it, so the box is
 * the right height before the image arrives and nothing shifts.
 */
function Scene({
  name, widths, ratio,
  portrait, portraitWidths, portraitRatio,
  sizes, alt, fallbackWidth, rounded = 3, sx,
}) {
  return (
    <Box
      sx={{
        position: "relative", overflow: "hidden", borderRadius: rounded,
        border: "1px solid", borderColor: "divider", bgcolor: "#0A0F14",
        aspectRatio: portrait ? { xs: portraitRatio, md: ratio } : ratio,
        ...sx,
      }}
    >
      <Box component="picture" sx={{ display: "block", width: "100%", height: "100%" }}>
        {portrait && <source media={PHONES} type="image/avif" srcSet={srcset(portrait, portraitWidths, "avif")} sizes={sizes} />}
        {portrait && <source media={PHONES} type="image/webp" srcSet={srcset(portrait, portraitWidths, "webp")} sizes={sizes} />}
        <source type="image/avif" srcSet={srcset(name, widths, "avif")} sizes={sizes} />
        <source type="image/webp" srcSet={srcset(name, widths, "webp")} sizes={sizes} />
        {/* A plain <img> keeps width/height as attributes (see HeroSection). */}
        <img
          src={`/landing/${name}-${fallbackWidth}.jpg`}
          alt={alt}
          loading="lazy"
          decoding="async"
          style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>
    </Box>
  );
}

/**
 * The reusable phone photograph with real HTML on its screen.
 *
 * The artwork is opaque and includes the room around the phone, so the screen
 * is addressed by its measured position inside the frame rather than by
 * cropping: left 27.3%, top 9%, width 45.5%, height 78% of the whole image.
 * Those percentages only hold while the image is shown complete, so the
 * wrapper fixes the source aspect ratio and never uses object-fit: cover.
 *
 * The screen is ~45% of the frame's width, so a 360px-wide frame leaves a
 * 164px screen — too narrow for readable interface text. Callers therefore
 * give it real width (the payment story runs it at up to 560px, where the
 * screen is ~255px, close to a phone at 0.65 scale) and keep what is on the
 * screen to a few large elements.
 */
export function PhoneStage({ children, maxWidth = 560, sx }) {
  return (
    <Box sx={{ position: "relative", width: "100%", maxWidth, mx: "auto", aspectRatio: "1122 / 1402", ...sx }}>
      <Box component="picture" sx={{ display: "block", width: "100%", height: "100%" }}>
        <source type="image/avif" srcSet={srcset("phone-stage", [480, 780], "avif")} sizes="(min-width: 900px) 560px, 92vw" />
        <source type="image/webp" srcSet={srcset("phone-stage", [480, 780], "webp")} sizes="(min-width: 900px) 560px, 92vw" />
        <img
          src="/landing/phone-stage-780.jpg"
          alt=""
          loading="lazy"
          decoding="async"
          style={{ display: "block", width: "100%", height: "100%" }}
        />
      </Box>
      <Box
        sx={{
          position: "absolute", left: "27.3%", top: "9%", width: "45.5%", height: "78%",
          // The screen's corner radius measured against the whole frame, split
          // per axis because the box is much taller than it is wide.
          borderRadius: "7.8% / 3.7%",
          overflow: "hidden",
          bgcolor: "#05090F",
          display: "grid",
          "& > *": { gridArea: "1 / 1" },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export function LocationsVisual() {
  const online = DEMO_LOCATIONS.filter((l) => l.online).length;
  return (
    <Box>
      <Scene
        name="routers-scene" widths={[700, 1200]} ratio="1448 / 1086" fallbackWidth={1200}
        portrait="routers-scene-portrait" portraitWidths={[720]} portraitRatio="1122 / 1402"
        sizes="(min-width: 900px) 400px, 92vw"
        alt="A hotspot router and a wireless access point on a desk, their link lights on"
        sx={{ maxWidth: 400, mx: "auto" }}
      />
      <Stack spacing={0.75} sx={{ mt: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary" }}>Your locations</Typography>
          <Pill>{online} of {DEMO_LOCATIONS.length} online</Pill>
        </Stack>
        {DEMO_LOCATIONS.slice(0, 3).map((l) => (
          <Stack key={l.id} direction="row" alignItems="center" spacing={1.5}
            sx={{ px: 1.5, py: 0.75, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", flexShrink: 0, bgcolor: l.online ? "success.main" : "text.disabled" }} />
            <Typography noWrap sx={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: "text.primary" }}>{l.name}</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
              {l.online ? `${l.users} connected` : "Offline"}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export function VouchersVisual() {
  return (
    <Box>
      <Scene
        name="vouchers-scene" widths={[560, 900]} ratio="1122 / 1402" fallbackWidth={900}
        sizes="(min-width: 900px) 280px, 62vw"
        alt="Three printed PesaFii voucher cards on a desk for 1 hour, 3 hours and 1 day, each with a scratch panel"
        sx={{ maxWidth: 280, mx: "auto" }}
      />
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
        {[["Batch", "50 vouchers · 3 Hours"], ["Price", "UGX 2,000 each"], ["Status", "38 available · 12 used"]].map(([k, v]) => (
          <Paper key={k} sx={{ flex: "1 1 30%", minWidth: 0, px: 1.5, py: 1, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontSize: 10.5, color: "text.secondary", fontWeight: 600 }}>{k}</Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.primary", fontWeight: 700 }}>{v}</Typography>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

export function WithdrawVisual() {
  const theme = useTheme();
  const green = theme.palette.primary.main;
  const Row = ({ k, v, strong }) => (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{k}</Typography>
      <Typography sx={{ fontSize: 13, color: "text.primary", fontWeight: strong ? 800 : 600 }}>{v}</Typography>
    </Stack>
  );
  return (
    <Card title="Withdraw to Mobile Money" icon={AccountBalanceWalletIcon}>
      <Box sx={{ p: 1.75, borderRadius: 2.5, mb: 1.5, bgcolor: alpha(green, theme.palette.mode === "dark" ? 0.1 : 0.07), border: `1px solid ${alpha(green, 0.25)}` }}>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 600 }}>Available balance</Typography>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "text.primary", lineHeight: 1.2 }}>UGX 3,920,000</Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        {["MTN Mobile Money", "Airtel Money"].map((n, i) => (
          <Box key={n} sx={{
            flex: 1, textAlign: "center", py: 0.9, borderRadius: 2, fontSize: 12.5, fontWeight: 700,
            border: "1px solid", borderColor: i === 0 ? "primary.main" : "divider",
            color: i === 0 ? "text.primary" : "text.secondary", bgcolor: i === 0 ? alpha(green, 0.08) : "transparent",
          }}>{n}</Box>
        ))}
      </Stack>
      <Box sx={{ borderTop: "1px solid", borderColor: "divider", mt: 1 }}>
        <Row k="Recipient gets" v="UGX 200,000" strong />
        <Row k="Fee" v="Shown before you confirm" />
      </Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25, px: 1.5, py: 1.1, borderRadius: 2, bgcolor: "action.hover" }}>
        <LockOutlinedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
        <Typography sx={{ flex: 1, fontSize: 12.5, color: "text.secondary" }}>Withdrawal PIN</Typography>
        <Typography sx={{ letterSpacing: "0.3em", color: "text.primary", fontWeight: 800 }}>••••</Typography>
      </Stack>
    </Card>
  );
}

export function PortalVisual() {
  // Two operators' login pages side by side on desktop; one on a phone, where
  // a pair would leave each of them too small to read.
  const portal = (name, alt, hide) => (
    <Scene
      key={name}
      name={name} widths={[400, 640]} ratio="1122 / 1402" fallbackWidth={640}
      sizes="(min-width: 900px) 230px, 70vw"
      alt={alt}
      sx={{ flex: 1, minWidth: 0, display: hide ? { xs: "none", sm: "block" } : "block" }}
    />
  );
  return (
    <Box>
      <Stack direction="row" spacing={2} justifyContent="center" sx={{ maxWidth: 470, mx: "auto" }}>
        {portal("portal-cafe", "A café's Wi-Fi login page on a phone, with its own name, photo and packages button")}
        {portal("portal-campus", "A campus Wi-Fi login page on a phone, branded differently from the café's", true)}
      </Stack>
      <Stack direction="row" useFlexGap flexWrap="wrap" justifyContent="center" gap={1} sx={{ mt: 2 }}>
        {["Your photo", "Your name & tagline", "Your colours", "Your support number"].map((t) => <Pill key={t} tone="muted">{t}</Pill>)}
      </Stack>
    </Box>
  );
}

export function ReportsVisual() {
  const theme = useTheme();
  const rows = [
    { name: "3 Hours · UGX 2,000", pct: 46 },
    { name: "24 Hours · UGX 5,000", pct: 31 },
    { name: "1 Hour · UGX 1,000", pct: 23 },
  ];
  const hours = [12, 18, 26, 30, 42, 58, 74, 92, 80, 54];
  return (
    <Card title="Sales this week" aside={<Pill>UGX 1,240,000</Pill>}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 1 }}>By package</Typography>
      <Stack spacing={1.25} sx={{ mb: 2.5 }}>
        {rows.map((r) => (
          <Box key={r.name}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 12.5, color: "text.primary", fontWeight: 600 }}>{r.name}</Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{r.pct}%</Typography>
            </Stack>
            <Box sx={{ height: 8, borderRadius: 99, bgcolor: "action.hover", overflow: "hidden" }}>
              <Box sx={{ height: "100%", width: `${r.pct}%`, borderRadius: 99, bgcolor: "primary.main" }} />
            </Box>
          </Box>
        ))}
      </Stack>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 1 }}>Busiest hours</Typography>
      <Stack direction="row" alignItems="flex-end" spacing={0.75} sx={{ height: 64 }}>
        {hours.map((h, i) => (
          <Box key={i} sx={{ flex: 1, height: `${h}%`, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.25 + (h / 100) * 0.6) }} />
        ))}
      </Stack>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography sx={{ fontSize: 10.5, color: "text.disabled" }}>8 am</Typography>
        <Typography sx={{ fontSize: 10.5, color: "text.disabled" }}>10 pm</Typography>
      </Stack>
    </Card>
  );
}

export function TeamVisual() {
  // The roles are the real templates; the scene is illustrative. The labels stay
  // HTML so they read at any size and stay correct when the templates change.
  const people = [
    { role: "Admin", scope: "Full access, including your team" },
    { role: "Manager", scope: "Vouchers, packages and sales" },
    { role: "Voucher Manager", scope: "Generates and prints vouchers" },
    { role: "Support Staff", scope: "Helps customers: vouchers, routers, users" },
    { role: "Voucher agent", scope: "Sells your vouchers, earns commission" },
  ];
  return (
    <Box>
      <Scene
        name="team-scene" widths={[700, 1200]} ratio="1536 / 1024" fallbackWidth={1200}
        portrait="team-scene-portrait" portraitWidths={[720]} portraitRatio="1122 / 1402"
        sizes="(min-width: 900px) 430px, 92vw"
        alt="Two people working together at a laptop in a café, with a stack of voucher cards on the table"
        sx={{ maxWidth: 430, mx: "auto" }}
      />
      <Stack spacing={0.75} sx={{ mt: 2 }}>
        {people.map((p) => (
          <Stack key={p.role} direction="row" alignItems="baseline" spacing={1.25}
            sx={{ px: 1.5, py: 0.7, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", whiteSpace: "nowrap" }}>{p.role}</Typography>
            <Typography noWrap sx={{ fontSize: 12, color: "text.secondary" }}>{p.scope}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
